import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { DosimetriaPenaResult } from "@/types/calculos";
import { addToHistory, useCalculoPrefill } from "@/hooks/useCalculoHistory";

export const DosimetriaPena = () => {
  const [penaMinima, setPenaMinima] = useState("");
  const [penaMaxima, setPenaMaxima] = useState("");
  const [antecedentes, setAntecedentes] = useState([0]);
  const [condutaSocial, setCondutaSocial] = useState([0]);
  const [personalidade, setPersonalidade] = useState([0]);
  const [motivos, setMotivos] = useState([0]);
  const [circunstancias, setCircunstancias] = useState([0]);
  const [consequencias, setConsequencias] = useState([0]);
  const [comportamento, setComportamento] = useState([0]);
  const [resultado, setResultado] = useState<DosimetriaPenaResult | null>(null);

  useCalculoPrefill("dosimetria", (v) => {
    setPenaMinima(v.penaMinima ?? "");
    setPenaMaxima(v.penaMaxima ?? "");
    setAntecedentes(v.antecedentes ?? [0]);
    setCondutaSocial(v.condutaSocial ?? [0]);
    setPersonalidade(v.personalidade ?? [0]);
    setMotivos(v.motivos ?? [0]);
    setCircunstancias(v.circunstancias ?? [0]);
    setConsequencias(v.consequencias ?? [0]);
    setComportamento(v.comportamento ?? [0]);
    setResultado(null);
  });

  const handleCalcular = async () => {
    if (!penaMinima || !penaMaxima) {
      return;
    }

    const fatores = [antecedentes[0], condutaSocial[0], personalidade[0], motivos[0], circunstancias[0], consequencias[0], comportamento[0]];
    const mediaFatores = fatores.reduce((acc, val) => acc + val, 0) / fatores.length;

    const penaBaseNumeros = parseFloat(penaMinima);

    setResultado({
      penaBase: penaBaseNumeros,
      penaFinal: penaBaseNumeros,
      aumentoPercentual: 0
    });

    addToHistory({
      type: "dosimetria",
      label: "Dosimetria da Pena",
      values: { penaMinima, penaMaxima, antecedentes, condutaSocial, personalidade, motivos, circunstancias, consequencias, comportamento },
      summary: `Pena base ${penaBaseNumeros} meses • Fatores analisados (média ${mediaFatores.toFixed(2)})`,
    });
  };

  const formatMeses = (meses: number) => {
    const anos = Math.floor(meses / 12);
    const mesesRestantes = Math.round(meses % 12);
    if (anos > 0) {
      return `${anos} ano${anos > 1 ? 's' : ''} e ${mesesRestantes} mês${mesesRestantes !== 1 ? 'es' : ''}`;
    }
    return `${mesesRestantes} mês${mesesRestantes !== 1 ? 'es' : ''}`;
  };

  const FatorSlider = ({ 
    label, 
    value, 
    onChange, 
    description 
  }: { 
    label: string; 
    value: number[]; 
    onChange: (value: number[]) => void;
    description: string;
  }) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="px-3">
        <Slider
          value={value}
          onValueChange={onChange}
          max={5}
          min={-2}
          step={1}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span>-2</span>
          <span>0</span>
          <span>+5</span>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dosimetria da Pena (Art. 59 CP)</CardTitle>
        <p className="text-sm text-muted-foreground">
          Análise qualitativa das circunstâncias judiciais do Art. 59 CP
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="pena-minima">Pena Mínima (meses)</Label>
            <Input
              id="pena-minima"
              type="number"
              placeholder="6"
              value={penaMinima}
              onChange={(e) => setPenaMinima(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="pena-maxima">Pena Máxima (meses)</Label>
            <Input
              id="pena-maxima"
              type="number"
              placeholder="24"
              value={penaMaxima}
              onChange={(e) => setPenaMaxima(e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <FatorSlider
              label="Antecedentes"
              value={antecedentes}
              onChange={setAntecedentes}
              description="Condenações anteriores transitadas em julgado"
            />
            
            <FatorSlider
              label="Conduta Social"
              value={condutaSocial}
              onChange={setCondutaSocial}
              description="Comportamento na vida em sociedade"
            />
            
            <FatorSlider
              label="Personalidade"
              value={personalidade}
              onChange={setPersonalidade}
              description="Aspectos psicológicos e morais"
            />
            
            <FatorSlider
              label="Motivos"
              value={motivos}
              onChange={setMotivos}
              description="Razões que levaram ao crime"
            />
          </div>

          <div className="space-y-4">
            <FatorSlider
              label="Circunstâncias"
              value={circunstancias}
              onChange={setCircunstancias}
              description="Como o crime foi praticado"
            />
            
            <FatorSlider
              label="Consequências"
              value={consequencias}
              onChange={setConsequencias}
              description="Resultados e danos causados"
            />
            
            <FatorSlider
              label="Comportamento"
              value={comportamento}
              onChange={setComportamento}
              description="Conduta posterior ao crime"
            />
          </div>
        </div>
        
        <Button 
          onClick={handleCalcular} 
          className="w-full"
        >
          Analisar Circunstâncias Judiciais
        </Button>
        
        {resultado && (
          <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
            <h3 className="font-semibold mb-2">Análise Qualitativa (Art. 59 CP):</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Parâmetros:</strong> {formatMeses(parseFloat(penaMinima))} a {formatMeses(parseFloat(penaMaxima))}</p>
                <p><strong>Pena Mínima Legal:</strong> {formatMeses(parseFloat(penaMinima))}</p>
              </div>
              <div>
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800">
                  <p><strong>⚖️ Orientação:</strong> A fixação da pena-base deve considerar qualitativamente as circunstâncias do Art. 59 do CP. Esta ferramenta serve apenas como guia para análise dos fatores judiciais.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};